<template>
  <v-card outlined class="card-center pb-2">
    <v-container class="px-4 py-4" fluid>
      <v-card-title class="px-0 py-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <validation-observer ref="observer" v-slot="{ invalid }">
        <v-card class="py-2" flat>
          <v-row dense class="mb-1">
            <v-col cols="4">
              <ManufacturerInput v-model="searchCondition.manufacturerCd" />
            </v-col>
            <v-col cols="4">
              <validation-provider v-slot="{ errors }" :name="$t('ELE0005')" rules="required">
                <AcceptCompanyInput
                  :label="$t('ELE0005')"
                  v-model="searchCondition.acceptCompanyCd"
                  :error-messages="errors"
                  required
                />
              </validation-provider>
            </v-col>
            <v-col>
              <TextFieldInput v-model="searchCondition.partCd" :label="$t('ELE0031')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <TextFieldInputWithDatePicker v-model="searchCondition.acceptDateFrom" :label="$t('ELE0201')" />
            </v-col>
            <v-card-text tag="span" class="my-auto"> {{ $t('ELE0141') }} </v-card-text>
            <v-col>
              <TextFieldInputWithDatePicker v-model="searchCondition.acceptDateTo" :label="$t('ELE0201')" />
            </v-col>
            <v-spacer />
            <v-col>
              <v-row class="justify-end my-0 mx-0">
                <Btn
                  class="mr-4"
                  :label="$t('ELE0040')"
                  icon="mdi-download"
                  @click="onClickDownload"
                  :disabled="invalid"
                />
                <BtnSearch @click="onClickSearch" :disabled="invalid" />
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </validation-observer>
      <v-card outlined>
        <v-card-title>
          {{ $t('ELE0023') }}
        </v-card-title>
        <v-data-table :headers="headers" :items="items" dense>
          <template v-slot:item.inspStatus="{ item }">
            <span v-if="item.inspStatus != ''">{{ $t('InspIndex.InspStatusCd.' + item.inspStatus) }} </span>
          </template>
          <template v-slot:item.xrfInspection="{ item }">
            <td
              v-if="item.xrfInspection != ''"
              class="v-data-table__divider"
              :style="{ 'background-color': item.xrfInspection == enumXrfInspNeedCd.NEED ? '#F8CED2' : '#BBD9F3' }"
            >
              {{ $t('XrfInspNeedCd.' + item.xrfInspection) }}
            </td>
            <td v-else class="v-data-table__divider"></td>
          </template>
          <!--項目名の先頭単語が短いため、v-datatable標準のalign:rightで右寄せすると、ヘッダーの先頭にソーターが付く-->
          <template v-slot:item.acceptBoxQty="{ item }">
            <td class="text-right v-data-table__divider">{{ item.acceptBoxQty }}</td>
          </template>
          <template v-slot:item.inspBoxQty="{ item }">
            <td class="text-right v-data-table__divider">{{ item.inspBoxQty }}</td>
          </template>
          <template v-slot:item.iqcInspResultCd="{ item }">
            <td class="v-data-table__divider" :style="{ 'background-color': getColorInspResult(item.iqcInspResultCd) }">
              {{ getEnumlang('InspResultCd.', item.iqcInspResultCd) }}
            </td>
          </template>
          <template v-slot:item.oqcInspResultCd="{ item }">
            <td class="v-data-table__divider" :style="{ 'background-color': getColorInspResult(item.oqcInspResultCd) }">
              {{ getEnumlang('InspResultCd.', item.oqcInspResultCd) }}
            </td>
          </template>
          <template v-slot:item.appInspResultCd="{ item }">
            <td class="v-data-table__divider" :style="{ 'background-color': getColorInspResult(item.appInspResultCd) }">
              {{ getEnumlang('InspResultCd.', item.appInspResultCd) }}
            </td>
          </template>
          <template v-slot:item.appNumOfInsp="{ item }">
            <td class="v-data-table__divider">
              {{ item.appNumOfInsp ? `(${$t('ELE0262')}) ` + item.appNumOfInsp : '' }}
            </td>
          </template>
          <template v-slot:item.dimInspResultCd="{ item }">
            <td class="v-data-table__divider" :style="{ 'background-color': getColorInspResult(item.dimInspResultCd) }">
              {{ getEnumlang('InspResultCd.', item.dimInspResultCd) }}
            </td>
          </template>
          <template v-slot:item.dimNumOfInsp="{ item }">
            <td class="v-data-table__divider">
              {{ item.dimNumOfInsp ? `(${$t('ELE0262')}) ` + item.dimNumOfInsp : '' }}
            </td>
          </template>
          <template v-slot:item.appDefectQty="{ item }">
            <td class="text-right v-data-table__divider">{{ item.appDefectQty }}</td>
          </template>
          <template v-slot:item.aqlSamplingSize="{ item }">
            <td class="text-right v-data-table__divider">{{ item.aqlSamplingSize }}</td>
          </template>
          <!-- <template v-slot:item.deemedInsp="{ item }">
            <td class="v-data-table__divider">
              {{ getEnumlang('InspAssumption.', item.deemedInsp) }}
            </td>
          </template> -->
        </v-data-table>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>
import moment from 'moment'
import { XrfInspNeedCd, InspResultCd } from '@/enum'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import { Storage } from '@/plugins/storage.js'

import Btn from '@/components/atoms/base/button/Btn'
import BtnDelete from '@/components/atoms/button/BtnDelete'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import BtnNew from '@/components/atoms/button/BtnNew'
import BtnLink from '@/components/atoms/base/button/BtnLink'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import IconInquiry from '@/components/atoms/icon/IconInquiry'

import SearchInspLotSubDialog from '@/components/organisms/terminal/dialog/SearchInspLotSubDialog'
import SearchInspLotDtlDialog from '@/components/organisms/terminal/dialog/SearchInspLotDtlDialog'
import InspAttachmentForm from '@/components/organisms/master/form/InspAttachmentForm'

import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import AcceptCompanyInput from '@/components/atoms/select/AcceptCompanyInput'
import InspFilteringMultipleInput from '@/components/atoms/select/InspFilteringMultipleInput'

export default {
  name: 'SearchInspLotForm',

  mixins: [APIUtils, Message, Loading, DateUtils],

  components: {
    ValidationObserver,
    ValidationProvider,
    Btn,
    BtnDelete,
    BtnSave,
    BtnSearch,
    BtnNew,
    BtnLink,
    TextFieldInput,
    TextFieldInputWithDatePicker,
    SelectSingleInput,
    IconInquiry,
    SearchInspLotSubDialog,
    SearchInspLotDtlDialog,
    InspAttachmentForm,
    ManufacturerInput,
    AcceptCompanyInput,
    InspFilteringMultipleInput,
  },

  props: {},

  computed: {
    enumXrfInspNeedCd: () => XrfInspNeedCd,
  },

  data() {
    return {
      subDialog: false,
      dtlDialog: false,
      inspAttachDialog: false,
      selectedSubItems: null,
      selectedDtlItems: null,
      selectedInspAttachItem: null,
      inspStatusItems: [],
      xrfInspJudgeItems: [],
      items: [],
      headers: [
        { text: this.$t('ELE0139'), value: 'inspStatus', divider: true },
        { text: this.$t('ELE0351'), value: 'iqcInspResultCd', divider: true },
        { text: this.$t('ELE0143'), value: 'inspFiltering', divider: true },
        { text: this.$t('ELE0115'), value: 'inspTimingName', divider: true },
        { text: this.$t('ELE0055'), value: 'acceptDatetime', divider: true },
        { text: this.$t('ELE0210'), value: 'inspEndDatetime', divider: true },
        { text: this.$t('ELE0134'), value: 'manufacturerCompanyName', divider: true },
        { text: this.$t('ELE0005'), value: 'acceptanceCompanyName', divider: true },
        { text: this.$t('ELE0049'), value: 'lotNo', divider: true },
        { text: this.$t('ELE0031'), value: 'partCd', divider: true },
        { text: this.$t('ELE0150'), value: 'acceptBoxQty', divider: true },
        { text: this.$t('ELE0151'), value: 'inspBoxQty', divider: true },
        { text: this.$t('ELE0161'), value: 'oqcInspIndexNo', divider: true },
        { text: this.$t('ELE0350'), value: 'oqcInspResultCd', divider: true },
        { text: this.$t('ELE0148'), value: 'xrfInspection', divider: true },
        { text: this.$t('ELE0149'), value: 'xrfDate', divider: true },
        { text: this.$t('ELE0160'), value: 'iqcInspIndexNo', divider: true },
        { text: this.$t('ELE0368'), value: 'everyKey', divider: true },
        { text: this.$t('ELE0211'), value: 'appInspResultCd', divider: true },
        { text: this.$t('ELE0366'), value: 'appNumOfInsp', divider: true },
        { text: this.$t('ELE0229'), value: 'dimInspResultCd', divider: true },
        { text: this.$t('ELE0367'), value: 'dimNumOfInsp', divider: true },
        { text: this.$t('ELE0157'), value: 'cav', divider: true },
        { text: this.$t('ELE0130'), value: 'shift', divider: true },
        { text: this.$t('ELE0224'), value: 'model', divider: true },
        { text: this.$t('ELE0145'), value: 'aql', divider: true },
        { text: this.$t('ELE0268'), value: 'appDefectQty', divider: true },
        { text: this.$t('ELE0176'), value: 'aqlSamplingSize', divider: true },
        { text: this.$t('ELE0359'), value: 'appProblems', divider: true },
        { text: this.$t('ELE0360'), value: 'dimProblems', divider: true },
        { text: this.$t('ELE0361'), value: 'appInspector', divider: true },
        { text: this.$t('ELE0362'), value: 'dimInspector', divider: true },
        { text: this.$t('ELE0358'), value: 'inspStartDate', divider: true },
        { text: this.$t('ELE0363'), value: 'appFinishDate', divider: true },
        { text: this.$t('ELE0364'), value: 'dimFinishDate', divider: true },
        // { text: this.$t('ELE0365'), value: 'deemedInsp', divider: true },
        { text: this.$t('ELE0369'), value: 'qrInfo', divider: false },
      ],
      searchCondition: {
        manufacturerCd: '',
        acceptCompanyCd: '',
        partCd: '',
        acceptDateFrom: moment().format('YYYY-MM-DD'),
        acceptDateTo: moment().format('YYYY-MM-DD'),
      },
    }
  },

  methods: {
    getEnumlang(key, value) {
      // langのenumからメッセージ取得
      let lang = ''
      if (value) {
        lang = this.$t(key + value)
      }
      return lang
    },
    getColorInspResult(inspResultCd) {
      // 検査結果に対応する色設定
      let color = ''

      if (inspResultCd == InspResultCd.OK) {
        color = '#EDEFFD'
      } else if (inspResultCd == InspResultCd.SA_OK) {
        color = '#FCF4A1'
      } else if (inspResultCd == InspResultCd.NG) {
        color = '#F8CED2'
      }

      return color
    },

    async onClickSearch() {
      if (moment(this.searchCondition.acceptDateFrom).add(2, 'd') <= moment(this.searchCondition.acceptDateTo)) {
        this.setErrorMessage(this.$t('MSG0096', { p0: '2' }))
        return
      }
      this.startLoading()
      try {
        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          manufacturerCd: this.searchCondition.manufacturerCd,
          acceptCompanyCd: this.searchCondition.acceptCompanyCd,
          partCd: this.searchCondition.partCd,
          acceptDateFrom: this.searchCondition.acceptDateFrom,
          acceptDateTo: this.searchCondition.acceptDateTo,
        }
        this.items = []
        const elements = await API.get('/box/inspLotSummary', params)
        elements.forEach((element) => {
          element['acceptDatetime'] = this.toLocalTime(element['acceptDatetime'])
          element['inspEndDatetime'] = this.toLocalTime(element['inspEndDatetime'])
          element['lotDate'] = this.toLocalDate(element['lotDate'])
          if (element['xrfDate']) {
            element['xrfDate'] = this.toLocalDate(element['xrfDate'])
          }
          if (element['inspStartDate']) {
            element['inspStartDate'] = this.toLocalTime(element['inspStartDate'])
          }
          if (element['appFinishDate']) {
            element['appFinishDate'] = this.toLocalTime(element['appFinishDate'])
          }
          if (element['dimFinishDate']) {
            element['dimFinishDate'] = this.toLocalTime(element['dimFinishDate'])
          }
          this.items.push(element)
        })
      } catch (error) {
        this.items = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0078') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickDownload() {
      if (moment(this.searchCondition.acceptDateFrom).add(7, 'd') <= moment(this.searchCondition.acceptDateTo)) {
        this.setErrorMessage(this.$t('MSG0096', { p0: '7' }))
        return
      }
      this.startLoading()
      try {
        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          manufacturerCd: this.searchCondition.manufacturerCd,
          acceptCompanyCd: this.searchCondition.acceptCompanyCd,
          partCd: this.searchCondition.partCd,
          acceptDateFrom: this.searchCondition.acceptDateFrom,
          acceptDateTo: this.searchCondition.acceptDateTo,
        }
        const filePath = await API.get('/box/inspLotSummary/file', params)
        Storage.downloadFile(filePath)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0078') })))
      } finally {
        this.stopLoading()
      }
    },
  },

  // mounted() {
  //   this.onClickSearch()
  // },
}
</script>
